import paper from "paper";
import { buildNoteGroup } from "../canvas/noteRenderer";
import {
  isNoteVisible,
  numberedNotes,
  resolveNoteAnchors,
} from "../model/notes";
import {
  isVisible,
  pieceStitchLines,
  projectConstructionLines,
  type Project,
  type Piece,
  type Decoration,
  type ConstructionLine,
  type StitchLine,
} from "../model/Project";
import { seamPathData as modelSeamPathData } from "../model/seamGeometry";
import {
  marksFromPlacements,
  hasOffPitchHoles,
  stitchConnectorPath,
  stitchRendersClosed,
  isStitchLoop,
} from "../model/stitching";
import { placedStitchHoles } from "../model/placedStitchHoles";
import { stitchHolePlacements } from "../model/stitching";
import { jointMarksByStitch, stitchJoints } from "../model/stitchJoints";
import { applyPieceLayout } from "../model/pieceTransform";
import type { StampLibrary } from "../stamps/StampLibrary";
import { placeStampsAlongPath } from "../stamps/lineStamping";
import { normalizeStampItemToBbox } from "../stamps/stampNormalize";
import { tileRegion } from "../stamps/tileFill";
import { PaperOffset } from "paperjs-offset";
import { cssVar } from "../colors";

/** Laser operation a content category is assigned to (#572). */
export type ExportOperation = "cut" | "score" | "engrave";

/** Which holes (if any) a stitch line contributes to a vector export
 *  (#572): the working-area window (today's default), every computed hole
 *  on the line, just the seam polylines, or nothing. */
export type StitchExportMode = "working-holes" | "all-holes" | "seams" | "off";

/** The "Optimized vector export" dialog's settings (#572) — content
 *  selection, per-category operation assignment with laser colors /
 *  hairline widths, and the size-calibration boxes. Absent = the basic
 *  one-click export (today's colors and content, unchanged). */
export interface VectorExportSettings {
  content: {
    pattern: boolean;
    constructionLines: boolean;
    stitching: StitchExportMode;
  };
  operations: {
    pattern: ExportOperation;
    constructionLines: ExportOperation;
    stitching: ExportOperation;
  };
  laser: {
    cutColor: string;
    /** Inside cuts — holes and cutouts (#447). Defaults to the cut color
     *  so nothing changes until the user picks a distinct one; lasers
     *  often color inner cuts separately to order them before the
     *  outline. Shares the cut hairline width. */
    insideCutColor: string;
    cutWidthMm: number;
    scoreColor: string;
    scoreWidthMm: number;
    /** Raster engraving keeps each item's ORIGINAL stroke widths — only
     *  the color is unified. */
    engraveColor: string;
  };
  calibration: { metric: boolean; imperial: boolean };
}

/** Issue #572's laser defaults: red hairline cuts, blue hairline scores,
 *  black engraving at original thickness. */
export const DEFAULT_LASER_SETTINGS: VectorExportSettings["laser"] = {
  cutColor: "#ff0000",
  insideCutColor: "#ff0000",
  cutWidthMm: 0.01,
  scoreColor: "#0000ff",
  scoreWidthMm: 0.01,
  engraveColor: "#000000",
};

/** Default dialog settings — mirrors the basic export's content (pattern +
 *  construction + working-area stitch holes) with the conventional
 *  operation per category. */
export const DEFAULT_VECTOR_EXPORT_SETTINGS: VectorExportSettings = {
  content: {
    pattern: true,
    constructionLines: true,
    stitching: "working-holes",
  },
  operations: { pattern: "cut", constructionLines: "score", stitching: "cut" },
  laser: { ...DEFAULT_LASER_SETTINGS },
  calibration: { metric: false, imperial: false },
};

export interface ExportOptions {
  /** Stroke width for cut paths (millimeters in user units). */
  cutStroke?: number;
  /** Include realistic preview filter for raster export. */
  includePreviewFilter?: boolean;
  /** Whether decorations are flattened into the decoration layer. */
  flattenDecorations?: boolean;
  /** Collapse every CAM layer stroke to black. Used by PDF / print export,
   *  where the red/blue/etc. operation colors aren't meaningful — SVG export
   *  keeps the per-operation convention. */
  monochrome?: boolean;
  /**
   * Draw the project's canvas notes (#157) onto the pattern.
   *
   * Defaults to FALSE, and the default matters: SVG / DXF export shares this
   * function, and those files drive a laser or a cutter. Note text landing in a
   * cut or score layer would be machine instructions, not annotation. Only the
   * print/PDF and raster paths — output a person reads — turn this on.
   */
  includeNotes?: boolean;
  /** Physical unit for the document's width / height attributes. Geometry is
   *  always authored in mm; this only relabels the outer document size so the
   *  SVG imports at the right size in the user's chosen units. Defaults to
   *  "mm"; the viewBox stays in mm user units either way. */
  unit?: "mm" | "in";
  /** Cricut-compatibility pass. Cricut Design Space's SVG parser rejects
   *  clipping paths and pattern fills ("Your file includes unsupported
   *  items") and is happiest with plain polylines. When set we drop the
   *  tiled-fill decorations (the only clip-mask geometry) and flatten every
   *  curve to line segments. The modern SVG export leaves all of this intact
   *  for full vector editors (Affinity / Illustrator / Inkscape). See #444. */
  cricut?: boolean;
  /** Ring the off-pitch / overflow stitch holes of matched followers so they're
   *  obvious on the printed pattern (#687). Set by the PDF / print path; left
   *  off for the CAM SVG / DXF exports, where an extra ring would be cut. */
  markOffPitch?: boolean;
  /** Draw the point markers that say which hole meets which (#693). Default
   *  TRUE: the print dialog's Content toggle turns them off, and every other
   *  caller keeps them. Still subject to `markOffPitch` — they are a pattern
   *  annotation, never a cut. */
  pointMarkers?: boolean;
  /** Letter the marked shared holes (#693) — behind the `point-letters` flag,
   *  which the caller resolves (this module stays flag-free, like
   *  `markOffPitch`). Off leaves the chevrons unlettered. */
  pointLetters?: boolean;
  /** Optimized vector export settings (#572) — see
   *  {@link VectorExportSettings}. Absent for the basic one-click export. */
  vector?: VectorExportSettings;
  /** Include the project's construction lines (default true). The print
   *  flow exposes this as a Content toggle (#870); when `vector` settings
   *  are present they own the decision instead. */
  includeConstructionLines?: boolean;
}

/** Resolve a category's stroke style from the laser settings: cut / score
 *  use their color + hairline width; engrave keeps original widths
 *  (width null) under a unified color. */
function operationStyle(
  op: ExportOperation,
  laser: VectorExportSettings["laser"],
): { color: string; widthMm: number | null } {
  switch (op) {
    case "cut":
      return { color: laser.cutColor, widthMm: laser.cutWidthMm };
    case "score":
      return { color: laser.scoreColor, widthMm: laser.scoreWidthMm };
    case "engrave":
      return { color: laser.engraveColor, widthMm: null };
  }
}

/** Flatness (mm) used when reducing Béziers to line segments for the Cricut
 *  export. Small enough that the faceting is invisible at cut scale. */
const CRICUT_FLATTEN_TOLERANCE = 0.1;

// CAM layer stroke colors. Industry conventions (red = cut, blue =
// score / hole, black = engrave) but kept in CSS so a future
// "export theme" can override them in one place. PDF export collapses
// them all to black via the `monochrome` option.
const CUT_COLOR = () => cssVar("--export-cut");
const HOLE_COLOR = () => cssVar("--export-hole");
const STITCH_COLOR = () => cssVar("--export-stitch");
const ENGRAVE_COLOR = () => cssVar("--export-engrave");
const DECOR_COLOR = () => cssVar("--export-decor");
const CONSTRUCTION_COLOR = () => cssVar("--export-construction");
const FILL_OUTLINE = "none";

/**
 * Serialize a project to layered SVG. Uses an offscreen Paper.js scope so
 * exports are independent of the on-screen view.
 */
export function projectToSvg(
  project: Project,
  library: StampLibrary,
  options: ExportOptions = {},
): {
  svg: string;
  bounds: { x: number; y: number; w: number; h: number };
  /** Flattened outline polygons of every printed leaf item (curves/arcs reduced
   *  to line segments), in the same (post-scale) coordinate system as
   *  `bounds`/the viewBox. Used to find blank space on the page that clears all
   *  geometry (e.g. for the scale-check squares, #577). Polygons — not bounding
   *  boxes — so a thin diagonal strap or a circle doesn't claim its empty
   *  corners. */
  obstacles: Array<Array<[number, number]>>;
} {
  const cutStroke = options.cutStroke ?? 0.2;

  const scope = new paper.PaperScope();
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  scope.setup(canvas);
  scope.activate();
  const root = new scope.Layer();
  root.name = "root";

  const cutLayer = new scope.Group();
  cutLayer.name = "cut";
  cutLayer.data = { exportLayerId: "cut" };
  // Seam guide lines — created before the hole layer so the punched holes
  // render on top of the line tracing the row.
  const stitchLayer = new scope.Group();
  stitchLayer.name = "stitch";
  stitchLayer.data = { exportLayerId: "stitch" };
  const holeLayer = new scope.Group();
  holeLayer.name = "hole";
  holeLayer.data = { exportLayerId: "hole" };
  const engraveLayer = new scope.Group();
  engraveLayer.name = "engrave";
  engraveLayer.data = { exportLayerId: "engrave" };
  const decorLayer = new scope.Group();
  decorLayer.name = "decoration";
  decorLayer.data = { exportLayerId: "decoration" };
  // Construction (guide) lines — created last so they sit on top of the
  // pattern, matching the on-screen z-order. Without them a project that
  // is still only guide lines (no pieces drawn yet) would export blank
  // (#424).
  const constructionLayer = new scope.Group();
  constructionLayer.name = "construction";
  constructionLayer.data = { exportLayerId: "construction" };

  // Optimized vector export (#572): per-category operation styles; null
  // fields fall back to the basic export's CSS-var colors + cutStroke.
  const vec = options.vector;
  const patternStyle = vec
    ? operationStyle(vec.operations.pattern, vec.laser)
    : null;
  // Inside cuts (#447): holes + cutouts get their own color, but only
  // when the pattern is CUT — score / engrave already unify the color
  // per operation and an inside distinction would fight that.
  const insideCutColor =
    vec && vec.operations.pattern === "cut" ? vec.laser.insideCutColor : null;
  const stitchStyle = vec
    ? operationStyle(vec.operations.stitching, vec.laser)
    : null;
  const constructionStyle = vec
    ? operationStyle(vec.operations.constructionLines, vec.laser)
    : null;
  const includePattern = vec ? vec.content.pattern : true;
  const stitchMode: StitchExportMode = vec
    ? vec.content.stitching
    : "working-holes";

  // Shared-hole joints (#693) are computed on the UNLAID project: the layout
  // moves parts apart for printing, and it is precisely their overlap in design
  // space that makes two holes partners. Applied to the laid-out copies by hole
  // INDEX, which the layout doesn't disturb — it translates a part, it doesn't
  // reorder its row.
  const jointMarks = jointMarksByStitch(stitchJoints(project, scope));
  if (includePattern || stitchMode !== "off") {
    for (const piece of project.pieces) {
      // Linked parts export at their cut-layout (Spread) position — bake the
      // `layout` offset into the geometry, since the exporter walks model data
      // rather than the Paper scene (#652). No-op for unlinked / un-laid-out parts.
      const laid = applyPieceLayout(piece, scope);
      addPieceToLayers(scope, laid, library, {
        cutLayer,
        stitchLayer,
        holeLayer,
        engraveLayer,
        decorLayer,
        cutStroke,
        cricut: options.cricut === true,
        markOffPitch: options.markOffPitch === true,
        pointMarkers: options.pointMarkers !== false,
        pointLetters: options.pointLetters === true,
        includePattern,
        stitchMode,
        patternStyle,
        insideCutColor,
        stitchStyle,
        engraveColor: vec ? vec.laser.engraveColor : null,
        jointMarks,
      });
    }
  }

  // Construction lines live at the project level, not under any piece, and
  // render on top of the whole pattern (#424).
  if (
    vec
      ? vec.content.constructionLines
      : (options.includeConstructionLines ?? true)
  ) {
    for (const line of projectConstructionLines(project)) {
      if (!isVisible(line)) continue;
      addConstructionLine(
        scope,
        line,
        constructionLayer,
        cutStroke,
        constructionStyle,
      );
    }
  }

  // Size-calibration boxes (#572): plain squares placed left of the
  // content, one per chosen system, on their own "calibration" layer at
  // the score style (they're checked with a ruler, not cut out).
  if (vec && (vec.calibration.metric || vec.calibration.imperial)) {
    const calLayer = new scope.Group();
    calLayer.name = "calibration";
    calLayer.data = { exportLayerId: "calibration" };
    const style = operationStyle("score", vec.laser);
    const b = root.bounds;
    const anchorX = (Number.isFinite(b.x) ? b.x : 0) - 8;
    let yCursor = Number.isFinite(b.y) ? b.y : 0;
    const putSquare = (sizeMm: number) => {
      const rect = new scope.Path.Rectangle(
        new scope.Rectangle(
          new scope.Point(anchorX - sizeMm, yCursor),
          new scope.Size(sizeMm, sizeMm),
        ),
      );
      rect.strokeColor = new scope.Color(style.color);
      rect.strokeWidth = style.widthMm ?? cutStroke;
      rect.fillColor = null;
      calLayer.addChild(rect);
      yCursor += sizeMm + 8;
    };
    if (vec.calibration.metric) putSquare(30); // 3 cm
    if (vec.calibration.imperial) putSquare(2 * 25.4); // 2 in
  }

  // Cricut pass: reduce every curve to line segments. Done before the unit
  // scale so the tolerance stays in mm. Circles/holes become many-sided
  // polygons, which Cricut cuts cleanly. (Modern export keeps the curves.)
  if (options.cricut) flattenCurves(root, CRICUT_FLATTEN_TOLERANCE);

  // Drop any geometry with non-finite coordinates. The browser's SVG renderer
  // silently skips NaN/Infinity path segments, but svg2pdf feeds every
  // coordinate to jsPDF, which throws ("Invalid argument passed to
  // Notes (#157) go on last so they sit above the pattern, the same order the
  // canvas draws them in — and before the prune / unit-scale passes so they
  // are measured, scaled and bounded like everything else. Off unless the
  // caller asks: see `includeNotes`.
  if (options.includeNotes) {
    for (const { note, number } of numberedNotes(project)) {
      if (!isNoteVisible(note)) continue;
      root.addChild(
        buildNoteGroup(
          note,
          number,
          resolveNoteAnchors(note, project, scope).map((a) => a.point),
          scope,
          {
            monochrome: options.monochrome === true,
          },
        ),
      );
    }
  }

  // jsPDF.scale") and aborts the whole PDF export. Pruning here keeps a single
  // bad stamp/path from breaking export and also keeps `root.bounds` finite.
  pruneNonFiniteItems(root);

  // Geometry is authored in mm. When exporting in inches, scale the whole
  // scene by 1/25.4 about the origin so every emitted number — path data,
  // viewBox, width/height — is in inches. Paper's `strokeScaling` (on by
  // default) carries the stroke widths down with it, so physical line weights
  // stay correct.
  const unit = options.unit ?? "mm";
  const mmPerUnit = unit === "in" ? 25.4 : 1;
  if (mmPerUnit !== 1) root.scale(1 / mmPerUnit, new scope.Point(0, 0));

  // Per-item obstacle polygons for blank-space detection (#577), collected
  // after any unit scaling so they line up with the viewBox below.
  const obstacles: Array<Array<[number, number]>> = [];
  collectObstacles(root, obstacles);

  const allBounds = root.bounds;
  const margin = 4 / mmPerUnit;
  const fallback = 100 / mmPerUnit;
  const x = allBounds.x - margin || 0;
  const y = allBounds.y - margin || 0;
  const w = allBounds.width + margin * 2 || fallback;
  const h = allBounds.height + margin * 2 || fallback;

  // Paper exportSVG returns SVGElement when asString is false.
  const svgEl = scope.project.exportSVG({
    asString: false,
    bounds: "content",
    matchShapes: false,
    embedImages: true,
  }) as SVGElement;
  svgEl.setAttribute(
    "viewBox",
    `${formatLength(x)} ${formatLength(y)} ${formatLength(w)} ${formatLength(h)}`,
  );
  svgEl.setAttribute("width", `${formatLength(w)}${unit}`);
  svgEl.setAttribute("height", `${formatLength(h)}${unit}`);
  svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  // Re-tag groups so consumers find <g id="cut"> etc. Paper labels them via a
  // generated id; we rewrite the immediate children to match logical names.
  retagLayers(svgEl);
  // Strip Paper.js's `data-paper-data` JSON blobs (our internal layer
  // metadata). Some downstream tools (e.g. Cricut Design Space) reject SVGs
  // carrying unexpected data attributes, and the JSON is meaningless outside
  // this app — so it has no place in either the SVG or the PDF export.
  stripPaperMetadata(svgEl);
  // Cricut rejects clip paths / pattern fills outright. We already skip the
  // tiled-fill decorations that produce them, but strip any residual
  // clipPath/mask defs and references so the file is guaranteed clean.
  if (options.cricut) stripClipping(svgEl);
  // Paper.js stamps default presentation attributes onto the root group as the
  // literal "none" — including `stroke-width="none"` (NaN → "Invalid argument
  // passed to jsPDF.scale") and `stroke-linejoin="none"` (jsPDF's
  // CapJoinStyles lookup throws "Line join style of 'none' is not
  // recognized"), either of which aborts the entire PDF / print export. Drop
  // any numeric attribute that isn't a finite number and any enum attribute
  // outside its SVG-spec value set so svg2pdf falls back to its own defaults.
  sanitizePresentationAttributes(svgEl);
  // Paper exports `stroke-width` and the dash pattern as the stored mm values
  // regardless of the scene scale, so after scaling geometry to inches the line
  // weights AND dashes must be scaled to match — otherwise a 0.2 mm stroke would
  // read as 0.2 in and the dashes would be ~25× too long (#574).
  if (mmPerUnit !== 1) scaleStrokeLengths(svgEl, 1 / mmPerUnit);
  // PDF / print export drops the CAM color convention and draws every line in
  // black; SVG export keeps the per-operation colors.
  if (options.monochrome) forceBlackStrokes(svgEl);

  const serialized = new XMLSerializer().serializeToString(svgEl);

  // Tear down offscreen scope.
  scope.project.remove();

  return {
    svg: serialized,
    bounds: { x, y, w, h },
    obstacles,
  };
}

/** Collect a flattened outline polygon for every leaf item (paths, stamps, …)
 *  under `item`, skipping containers. Curves and arcs are reduced to line
 *  segments so the result is a simple polygon; items without segment geometry
 *  (shapes, rasters) fall back to their bounding-box rectangle. Polygons — not
 *  bounding boxes — so the empty space inside a thin diagonal strap's bbox, or a
 *  circle's corners, stays available for the scale-check squares (#577). */
function collectObstacles(
  item: paper.Item,
  out: Array<Array<[number, number]>>,
): void {
  if (item.children) {
    for (const child of item.children) collectObstacles(child, out);
    return;
  }
  // Paper Paths expose `flatten` + `segments`; flatten a clone so the exported
  // geometry keeps its curves.
  const path = item as paper.Path;
  if (typeof path.flatten === "function" && Array.isArray(path.segments)) {
    const clone = path.clone({ insert: false }) as paper.Path;
    // 2 mm faceting — finer than the placement search needs, coarse enough to
    // stay cheap on big patterns.
    clone.flatten(2);
    const pts: Array<[number, number]> = [];
    for (const seg of clone.segments) {
      const p = seg.point;
      if (Number.isFinite(p.x) && Number.isFinite(p.y)) pts.push([p.x, p.y]);
    }
    clone.remove();
    if (pts.length >= 2) out.push(pts);
    return;
  }
  // Fallback: a bounding-box rectangle as a 4-point polygon.
  const b = item.strokeBounds;
  if (
    Number.isFinite(b.x) &&
    Number.isFinite(b.y) &&
    Number.isFinite(b.width) &&
    Number.isFinite(b.height) &&
    b.width >= 0 &&
    b.height >= 0
  ) {
    out.push([
      [b.x, b.y],
      [b.x + b.width, b.y],
      [b.x + b.width, b.y + b.height],
      [b.x, b.y + b.height],
    ]);
  }
}

/**
 * Recursively remove dead geometry from the scene before export:
 *  - leaf items whose bounds aren't finite (a single NaN control point poisons
 *    the bounds, so the path/stamp is dropped wholesale — the browser would
 *    have skipped it anyway), and
 *  - any container that ends up empty, including the named export layers, so
 *    the SVG never carries an empty `<g>`.
 * The root layer itself is never removed (it anchors the export).
 */
function pruneNonFiniteItems(item: paper.Item): void {
  // Containers (Group / Layer / CompoundPath) expose a `children` array;
  // leaf geometry (Path / Shape / Raster) does not.
  if (item.children) {
    // Snapshot — removing children mutates the live array.
    for (const child of [...item.children]) pruneNonFiniteItems(child);
    if (item.children.length === 0 && item.className !== "Layer") {
      item.remove();
    }
    return;
  }
  const b = item.bounds;
  if (
    !Number.isFinite(b.x) ||
    !Number.isFinite(b.y) ||
    !Number.isFinite(b.width) ||
    !Number.isFinite(b.height)
  ) {
    item.remove();
  }
}

/**
 * Presentation attributes svg2pdf reads with parseFloat and uses directly as a
 * jsPDF length/coordinate. Any value that isn't a finite number (Paper emits
 * "none" for several of these) is stripped so svg2pdf uses its own default
 * rather than propagating NaN into jsPDF.
 */
const NUMERIC_SVG_ATTRS = [
  "stroke-width",
  "stroke-dashoffset",
  "stroke-miterlimit",
  "font-size",
  "opacity",
  "fill-opacity",
  "stroke-opacity",
];

/**
 * Enum presentation attributes svg2pdf passes verbatim into jsPDF, which
 * validates them against its CapJoinStyles table and THROWS on anything
 * unknown — killing the whole PDF / print export. Paper.js writes these on
 * the root group as the literal "none" whenever its children disagree (e.g.
 * a stamp authored with round joins next to default miter geometry), so any
 * project with a stamp used to abort with "Line join style of 'none' is not
 * recognized". Values outside the SVG spec's set are stripped so svg2pdf
 * falls back to its default (miter / butt), matching what browsers do with
 * an invalid value.
 */
const ENUM_SVG_ATTRS: ReadonlyArray<[string, ReadonlySet<string>]> = [
  ["stroke-linejoin", new Set(["miter", "round", "bevel"])],
  ["stroke-linecap", new Set(["butt", "round", "square"])],
];

/** Format a length for an SVG attribute: up to 4 decimals, trailing zeros and
 *  any bare decimal point trimmed. */
function formatLength(value: number): string {
  return value.toFixed(4).replace(/\.?0+$/, "");
}

/** SVG path data for a stitch line's seam polyline. Returns "" for a line
 *  with fewer than two vertices (nothing to trace). */
/** The seam's outline for export. Delegates to the model's `seamPathData` so a
 *  CURVED seam (#982) exports as the curve rather than the chords between its
 *  anchors — the exported guide has to be the same shape the holes were laid
 *  along, or a laser would score a line the punches don't follow. */
function seamPathData(line: StitchLine, closed: boolean): string {
  return modelSeamPathData({ ...line, closed });
}

/** Stroke length attributes Paper exports as the stored mm value regardless of
 *  the scene scale — line weight plus the dash pattern / offset. They must all
 *  be multiplied by `factor` to land in the scaled (inch) space; missing the
 *  dash array left the dashes at mm magnitude, so they read far too large in the
 *  inch-remapped (Cricut) export (#574). */
const SCALED_STROKE_ATTRS = [
  "stroke-width",
  "stroke-dasharray",
  "stroke-dashoffset",
];

/** Multiply every finite length in the stroke length attributes by `factor`.
 *  `stroke-dasharray` is a list (e.g. "6 4"), so each entry is scaled; "none"
 *  and non-numeric values are left untouched. */
function scaleStrokeLengths(svgEl: SVGElement, factor: number) {
  const elements: Element[] = [svgEl, ...svgEl.querySelectorAll("*")];
  for (const el of elements) {
    for (const attr of SCALED_STROKE_ATTRS) {
      const value = el.getAttribute(attr);
      if (value === null || value === "none") continue;
      const scaled = value
        .split(/[\s,]+/)
        .filter((s) => s.length > 0)
        .map((s) => {
          const n = parseFloat(s);
          return Number.isFinite(n) ? formatLength(n * factor) : s;
        })
        .join(" ");
      if (scaled.length > 0) el.setAttribute(attr, scaled);
    }
  }
}

function sanitizePresentationAttributes(svgEl: SVGElement) {
  const elements: Element[] = [svgEl, ...svgEl.querySelectorAll("*")];
  for (const el of elements) {
    for (const name of NUMERIC_SVG_ATTRS) {
      const value = el.getAttribute(name);
      if (value !== null && !Number.isFinite(parseFloat(value))) {
        el.removeAttribute(name);
      }
    }
    for (const [name, valid] of ENUM_SVG_ATTRS) {
      const value = el.getAttribute(name);
      if (value !== null && !valid.has(value)) {
        el.removeAttribute(name);
      }
    }
  }
}

/** Force every stroked element to black. Fills are left alone — only the
 *  cut/score/engrave *lines* are recolored. */
function forceBlackStrokes(svgEl: SVGElement) {
  const elements: Element[] = [svgEl, ...svgEl.querySelectorAll("*")];
  for (const el of elements) {
    const stroke = el.getAttribute("stroke");
    if (stroke !== null && stroke !== "none") {
      el.setAttribute("stroke", "#000000");
    }
  }
}

/** Recursively reduce every Path's curves to straight line segments. Paper's
 *  `flatten` rewrites the path in place; compound paths/groups are walked into
 *  their leaf Paths. Used by the Cricut export only. */
function flattenCurves(item: paper.Item, tolerance: number) {
  if (item instanceof (paper as unknown as { Path: typeof paper.Path }).Path) {
    (item as paper.Path).flatten(tolerance);
    return;
  }
  if (item.children) {
    for (const child of [...item.children]) flattenCurves(child, tolerance);
  }
}

/** Remove clip-path / mask geometry and references. Cricut Design Space
 *  reports "unsupported items" on any clip path or pattern fill, so the Cricut
 *  export must not carry them. */
function stripClipping(svgEl: SVGElement) {
  for (const el of Array.from(
    svgEl.querySelectorAll("clipPath, mask, pattern"),
  )) {
    el.remove();
  }
  const elements: Element[] = [svgEl, ...svgEl.querySelectorAll("*")];
  for (const el of elements) {
    el.removeAttribute("clip-path");
    el.removeAttribute("mask");
  }
}

/** Remove Paper.js metadata attributes (`data-paper-data`, holding JSON) from
 *  every element so they never reach the exported SVG / PDF. */
function stripPaperMetadata(svgEl: SVGElement) {
  const elements: Element[] = [svgEl, ...svgEl.querySelectorAll("*")];
  for (const el of elements) {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith("data-paper")) el.removeAttribute(attr.name);
    }
  }
}

function retagLayers(svgEl: SVGElement) {
  const groups = svgEl.querySelectorAll("g[id]");
  for (const g of groups) {
    const idAttr = g.getAttribute("id") ?? "";
    if (
      idAttr.startsWith("cut") ||
      idAttr.startsWith("stitch") ||
      idAttr.startsWith("hole") ||
      idAttr.startsWith("engrave") ||
      idAttr.startsWith("decoration") ||
      idAttr.startsWith("construction") ||
      idAttr.startsWith("calibration") ||
      idAttr.startsWith("root")
    ) {
      g.setAttribute("id", idAttr.split("-")[0] ?? idAttr);
    }
  }
}

/**
 * Add one construction (guide) line to the construction layer. Uses the
 * line's committed `pathData` (rounded corners already baked in) and a
 * dashed green stroke so it reads as a guide rather than a cut path. The
 * dash is expressed in mm; for inch exports `scaleStrokeLengths` rescales the
 * emitted `stroke-dasharray` into the inch space so the dash stays proportional
 * (#574).
 */
function addConstructionLine(
  scope: paper.PaperScope,
  line: ConstructionLine,
  layer: paper.Group,
  cutStroke: number,
  /** Optimized-export operation style (#572) — recolors the guide to its
   *  operation and thins it to the hairline; the dash stays (a dashed
   *  score is the usual fold / guide-line convention), sized from
   *  `cutStroke` so hairline widths don't shrink it to a near-solid. */
  style: { color: string; widthMm: number | null } | null = null,
) {
  const path = parsePath(scope, line.pathData);
  if (!path) return;
  path.strokeColor = new scope.Color(
    style ? style.color : CONSTRUCTION_COLOR(),
  );
  path.strokeWidth = style ? (style.widthMm ?? cutStroke) : cutStroke;
  path.dashArray = [cutStroke * 6, cutStroke * 4];
  path.fillColor = null;
  layer.addChild(path);
}

interface LayerSet {
  cutLayer: paper.Group;
  stitchLayer: paper.Group;
  holeLayer: paper.Group;
  engraveLayer: paper.Group;
  decorLayer: paper.Group;
  cutStroke: number;
  /** Cricut export — skip the clip-masked tiled fills it can't import. */
  cricut: boolean;
  /** Ring off-pitch stitch holes (#687) — print / PDF only. */
  markOffPitch: boolean;
  pointMarkers: boolean;
  pointLetters: boolean;
  /** Include the pattern itself (outline / holes / cutouts / decorations)
   *  — the optimized export can turn it off to export stitching alone. */
  includePattern: boolean;
  /** Which stitch geometry to emit (#572). */
  stitchMode: StitchExportMode;
  /** Operation styles (#572); null = the basic export's colors. */
  patternStyle: { color: string; widthMm: number | null } | null;
  /** Inside-cut color for holes + cutouts (#447). Set only when the
   *  pattern operation is "cut" — score / engrave keep their unified
   *  color. Null = basic export (holes keep their own CSS color). */
  insideCutColor: string | null;
  stitchStyle: { color: string; widthMm: number | null } | null;
  engraveColor: string | null;
  /** Shared-hole joint marks (#693) by stitch id, computed on the unlaid
   *  project — see the call site. */
  jointMarks: Map<string, Array<{ index: number; label: string }>>;
}

function addPieceToLayers(
  scope: paper.PaperScope,
  piece: Piece,
  library: StampLibrary,
  layers: LayerSet,
) {
  const patternColor = (fallback: string) =>
    layers.patternStyle ? layers.patternStyle.color : fallback;
  const patternWidth = () =>
    layers.patternStyle
      ? (layers.patternStyle.widthMm ?? layers.cutStroke)
      : layers.cutStroke;
  if (layers.includePattern) {
    const shape = parsePath(scope, piece.shapePathData);
    if (shape) {
      shape.strokeColor = new scope.Color(patternColor(CUT_COLOR()));
      shape.strokeWidth = patternWidth();
      shape.fillColor = null;
      layers.cutLayer.addChild(shape);
    }
    // Inside cuts (#447): holes and cutouts take the dedicated
    // inside-cut color when the optimized export set one (pattern
    // operation "cut"); the basic export keeps its per-category CSS
    // colors and other operations keep their unified color.
    const insideColor = (fallback: string) =>
      layers.insideCutColor ?? patternColor(fallback);
    for (const hole of piece.holes) {
      const c = new scope.Path.Circle(
        new scope.Point(hole.position.x, hole.position.y),
        hole.radius,
      );
      c.strokeColor = new scope.Color(insideColor(HOLE_COLOR()));
      c.strokeWidth = patternWidth();
      c.fillColor = null;
      layers.holeLayer.addChild(c);
    }
    for (const cutout of piece.cutouts) {
      const p = parsePath(scope, cutout.pathData);
      if (!p) continue;
      p.strokeColor = new scope.Color(insideColor(CUT_COLOR()));
      p.strokeWidth = patternWidth();
      p.fillColor = null;
      layers.cutLayer.addChild(p);
    }
  }
  const stitchColor = (fallback: string) =>
    layers.stitchStyle ? layers.stitchStyle.color : fallback;
  for (const line of pieceStitchLines(piece)) {
    if (layers.stitchMode === "off") break;
    if (!isVisible(line)) continue;
    // Retain only the holes on this piece's leather (#910) — or, for the
    // optimized export's "all holes" mode (#572), every computed hole on
    // the line. `piece` here is the laid-out copy (offset already baked in
    // by `applyPieceLayout`), so its stitch lines and `shapePathData` share
    // one space — filter, then build the marks so overflow holes are
    // dropped from the export, not just clipped.
    const marks =
      layers.stitchMode === "seams"
        ? []
        : marksFromPlacements(
            line,
            layers.stitchMode === "all-holes"
              ? stitchHolePlacements(line)
              : placedStitchHoles(line, piece, scope),
            layers.jointMarks.get(line.id),
          );
    // Thin guide line tracing the seam itself, drawn under the holes so the
    // stitcher can see the path the row follows. Magenta on its own stitch
    // layer, while the punched holes stay blue. On the PRINT / PDF pattern an
    // off-pitch follower's seam line is drawn as the WAVY hole-connector
    // instead of the straight seam, signalling the stitches there should be
    // tighter (#687) — kept out of the CAM SVG / DXF, where it would be cut.
    const wavy =
      layers.markOffPitch && hasOffPitchHoles(marks)
        ? stitchConnectorPath(
            marks,
            stitchRendersClosed(line),
            line.matchSpacing,
          )
        : null;
    // An inset-windowed loop (#704) stitches as an arc, so its seam line is
    // open — no closing segment across the gap.
    const seam = wavy ?? seamPathData(line, stitchRendersClosed(line));
    // Hole-only stitch modes (#572) skip the seam guide — a laser would cut
    // or score it; the basic export and "seams" mode keep it.
    const includeSeam =
      layers.stitchMode === "seams" || layers.stitchStyle === null;
    if (seam && includeSeam) {
      const sp = parsePath(scope, seam);
      if (sp) {
        sp.strokeColor = new scope.Color(stitchColor(STITCH_COLOR()));
        sp.strokeWidth = layers.stitchStyle
          ? (layers.stitchStyle.widthMm ?? layers.cutStroke / 2)
          : layers.cutStroke / 2;
        sp.fillColor = null;
        layers.stitchLayer.addChild(sp);
      }
    }
    for (const mark of marks) {
      const p = parsePath(scope, mark.d);
      if (!p) continue;
      p.strokeColor = new scope.Color(stitchColor(HOLE_COLOR()));
      p.strokeWidth = layers.stitchStyle
        ? (layers.stitchStyle.widthMm ?? layers.cutStroke)
        : layers.cutStroke;
      p.fillColor = null;
      layers.holeLayer.addChild(p);
      // Main-side skip slash (#687): an annotation on every Nth hole, on the
      // PRINT / PDF pattern only — kept out of the CAM SVG / DXF (not a cut).
      if (mark.skipD && layers.markOffPitch) {
        const slash = parsePath(scope, mark.skipD);
        if (slash) {
          slash.strokeColor = new scope.Color(STITCH_COLOR());
          slash.strokeWidth = layers.cutStroke;
          slash.fillColor = null;
          layers.stitchLayer.addChild(slash);
        }
      }
      // Start marker (#693): where to begin sewing this seam so it lines up
      // with its partner. Like the skip slash it is a PATTERN annotation, not a
      // cut, so it rides the same print / PDF gate and stays out of the CAM
      // SVG / DXF — a laser would happily cut the ring out of the leather.
      if (mark.startD && layers.markOffPitch && layers.pointMarkers) {
        const glyph = parsePath(scope, mark.startD);
        if (glyph) {
          glyph.strokeColor = new scope.Color(STITCH_COLOR());
          glyph.strokeWidth = layers.cutStroke;
          glyph.fillColor = null;
          layers.stitchLayer.addChild(glyph);
        }
      }
      // …and the letter naming the pair, so a part sewn to two others says
      // which ring meets which part. Same annotation gate as the ring.
      if (
        mark.pairLabelD &&
        layers.markOffPitch &&
        layers.pointMarkers &&
        layers.pointLetters
      ) {
        const text = parsePath(scope, mark.pairLabelD);
        if (text) {
          text.strokeColor = new scope.Color(STITCH_COLOR());
          text.strokeWidth = layers.cutStroke;
          text.fillColor = null;
          layers.stitchLayer.addChild(text);
        }
      }
    }
  }
  if (layers.includePattern) {
    for (const dec of piece.decorations) {
      if (!dec.visible) continue;
      addDecoration(scope, dec, library, layers);
    }
  }
}

function addDecoration(
  scope: paper.PaperScope,
  dec: Decoration,
  library: StampLibrary,
  layers: LayerSet,
) {
  switch (dec.kind) {
    case "stamp":
      addStamp(
        scope,
        library,
        dec.stampId,
        { x: dec.position.x, y: dec.position.y },
        dec.rotation,
        dec.scale,
        layers.decorLayer,
      );
      break;
    case "lineStamp": {
      const path = parsePath(scope, dec.pathData);
      if (!path) return;
      path.closed = dec.closed;
      const meta = library.get(dec.stampId);
      if (!meta) return;
      const placements = placeStampsAlongPath(path, meta, {
        spacing: dec.spacing,
        scale: dec.scale,
        rotateAlong: dec.rotateAlong,
        closed: dec.closed,
      });
      path.remove();
      for (const place of placements) {
        addStamp(
          scope,
          library,
          dec.stampId,
          place,
          place.rotation,
          place.scale,
          layers.decorLayer,
        );
      }
      break;
    }
    case "fill": {
      // Tiled fills are realized as a clip-masked group; Cricut rejects clip
      // paths, so omit them from the Cricut export (#444).
      if (layers.cricut) return;
      const region = parsePath(scope, dec.regionPathData);
      if (!region) return;
      region.closed = true;
      const meta = library.get(dec.stampId);
      if (!meta || !meta.tile) return;
      const placements = tileRegion(region, meta, {
        rotation: dec.rotation,
        scale: dec.scale,
      });
      const clip = region.clone({ insert: false });
      clip.clipMask = true;
      const clipGroup = new scope.Group([clip]);
      for (const place of placements) {
        addStamp(
          scope,
          library,
          dec.stampId,
          place,
          place.rotation,
          place.scale,
          clipGroup,
        );
      }
      region.remove();
      layers.decorLayer.addChild(clipGroup);
      break;
    }
    case "offset": {
      const src = parsePath(scope, dec.sourcePathData);
      if (!src) return;
      try {
        const off = PaperOffset.offset(src, dec.distance, {
          join: dec.joinType,
          cap: "butt",
        });
        off.strokeColor = new scope.Color(
          layers.engraveColor ?? ENGRAVE_COLOR(),
        );
        off.strokeWidth = layers.cutStroke;
        off.fillColor = null;
        layers.engraveLayer.addChild(off);
      } catch (e) {
        console.warn("offset export failed", e);
      }
      src.remove();
      break;
    }
    case "freePath": {
      const p = parsePath(scope, dec.pathData);
      if (!p) return;
      p.closed = dec.closed;
      p.strokeColor = new scope.Color(DECOR_COLOR());
      p.strokeWidth = dec.strokeWidth;
      p.fillColor = null;
      layers.decorLayer.addChild(p);
      break;
    }
  }
}

function addStamp(
  scope: paper.PaperScope,
  library: StampLibrary,
  stampId: string,
  position: { x: number; y: number },
  rotation: number,
  scale: number,
  parent: paper.Item,
) {
  const meta = library.get(stampId);
  if (!meta) return;
  let item: paper.Item;
  try {
    item = scope.project.importSVG(meta.svg, {
      expandShapes: true,
      insert: false,
    }) as paper.Item;
  } catch (e) {
    console.warn("stamp import failed", e);
    return;
  }
  // Map the raw SVG (authoring viewBox units) onto the declared physical
  // bbox first — the canvas does the same in `StampSymbols.parse`. Without
  // this the exported stamp keeps its viewBox size, which is both the wrong
  // scale AND the wrong frame for the bbox-relative centring below (#945).
  normalizeStampItemToBbox(item, meta, scope);
  const cx = meta.bbox.x + meta.bbox.w / 2;
  const cy = meta.bbox.y + meta.bbox.h / 2;
  item.translate(new scope.Point(-cx, -cy));
  item.scale(scale, scale, new scope.Point(0, 0));
  if (rotation) item.rotate(rotation, new scope.Point(0, 0));
  item.translate(new scope.Point(position.x, position.y));
  // Force decoration strokes to be hairline-ish.
  forceHairline(item);
  parent.addChild(item);
}

function forceHairline(item: paper.Item) {
  if ("strokeWidth" in item) {
    const sw = (item as paper.Path).strokeWidth;
    if (sw) (item as paper.Path).strokeWidth = Math.min(sw, 0.2);
  }
  if (item.children) for (const c of item.children) forceHairline(c);
}

function parsePath(
  scope: paper.PaperScope,
  d: string,
): paper.Path | paper.CompoundPath | null {
  if (!d) return null;
  try {
    const cp = new scope.CompoundPath(d);
    if (cp.children?.length === 1) {
      const single = cp.children[0] as paper.Path;
      single.remove();
      cp.remove();
      return single;
    }
    return cp;
  } catch (e) {
    console.warn("parsePath failed", e);
    return null;
  }
}
